/**
 * Root component.
 *
 * Auth-state machine (no zustand — just `onAuthStateChanged`):
 *   - `loading`: Firebase hasn't told us yet whether someone's signed in
 *   - `signed-out`: render <SignIn />
 *   - `unauthorized`: signed in but no `moderator` claim — render
 *     <AccessDenied />
 *   - `moderator`: render the routed app (Submissions list + detail)
 *
 * Why no router-level guard component: there are only 2 routes and the
 * branch above already covers everything. A `<RequireRole>` wrapper
 * would just re-do the same check.
 */
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { onAuthStateChanged, type User } from 'firebase/auth';

import { getFirebaseAuth, signOut as firebaseSignOut } from './lib/firebase';
import { readRole, type Role } from './lib/role';
import SignIn from './pages/SignIn';
import Submissions from './pages/Submissions';
import SubmissionDetail from './pages/SubmissionDetail';
import Header from './components/Header';

type AuthState =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'authed'; user: User; role: Role };

function AccessDenied({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  return (
    <div className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center px-6 text-center">
      <h1 className="text-2xl font-semibold text-slate-900">Access denied</h1>
      <p className="mt-3 text-sm text-slate-600">
        You're signed in as <span className="font-medium">{email}</span>, but your account doesn't
        have moderator permissions.
      </p>
      <p className="mt-3 text-sm text-slate-600">
        Ask a project admin to grant you the moderator claim, then refresh this page. From the
        <code className="mx-1 rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">
          opensoi-backend
        </code>
        repo:
      </p>
      <pre className="mt-3 overflow-x-auto rounded bg-slate-900 p-3 text-left text-xs text-slate-100">
{`go run scripts/grant-moderator.go --email ${email}`}
      </pre>
      <a
        className="mt-6 text-sm font-medium text-soi-600 hover:text-soi-700"
        href="mailto:dean.taler@sentra.io?subject=OpenSoi%20moderator%20access"
      >
        Or email a project admin
      </a>
      <button
        type="button"
        onClick={onSignOut}
        className="mt-8 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
      >
        Sign out
      </button>
    </div>
  );
}

export default function App() {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });

  useEffect(() => {
    const auth = getFirebaseAuth();
    return onAuthStateChanged(auth, async (user) => {
      if (user === null) {
        setState({ kind: 'signed-out' });
        return;
      }
      try {
        const role = await readRole(user, /* forceRefresh */ true);
        setState({ kind: 'authed', user, role });
      } catch {
        // If we can't read the token at all (e.g. revoked account),
        // treat as signed-out and let the user retry.
        setState({ kind: 'signed-out' });
      }
    });
  }, []);

  if (state.kind === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  if (state.kind === 'signed-out') {
    return <SignIn />;
  }

  if (state.role === 'unauthorized') {
    return (
      <AccessDenied
        email={state.user.email ?? '(unknown email)'}
        onSignOut={() => {
          firebaseSignOut().catch(() => {
            // best-effort — onAuthStateChanged will still fire on success
          });
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        email={state.user.email ?? '(unknown)'}
        onSignOut={() => {
          firebaseSignOut().catch(() => undefined);
        }}
      />
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-6 sm:px-6 lg:px-8">
        <Routes>
          <Route path="/" element={<Submissions />} />
          <Route path="/submissions/:id" element={<SubmissionDetail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
